import { getMeta } from "app/utils/meta";
import { getLoaderTrans } from "app/.server/utils";
import { Checkbox, CheckboxGroup } from "app/components/form/checkbox";
import { Radio, RadioGroup } from "app/components/form/radio";
import InputField from "app/components/form/input-field";
import PhoneField from "app/components/form/phone-field";
import TextArea from "app/components/form/textarea";
import Layout from "app/components/layout";
import Sprite from "app/components/sprite";
import {
  ZBNI_LEAD_GEN_ZAPIER_ENDPOINT,
  ZBNI_RECAPTCHA_SITE_KEY,
} from "app/config/constants";
import { useI18nLanguage } from "app/hooks/i18n";
import { getAccessToken } from "app/utils/access-token";
import { zapierClient } from "app/utils/axios";
import { verifyCaptcha } from "app/utils/captcha";
import classNames from "classnames";
import { Form, Formik } from "formik";
import { useRef, useState } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as yup from "yup";
import "app/utils/schema/phone";

export const loader = async ({ request, params }) => {
  const trans = await getLoaderTrans({ request, params });

  return {
    meta: {
      // TODO: Check this with the team
      title: `${trans("Request a demo")} | ${trans("Request a demo")}`,
    },
  };
};

export const meta = ({ loaderData: data }) => {
  return getMeta({
    title: data.meta.title,
  });
};

const RequestADemo = () => {
  const { t: trans } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [lookingForOtherField, showLookingForOtherField] = useState(false);
  const [hearAboutUsOtherField, showHearAboutUsOtherField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successPage, showSuccessPage] = useState(false);
  const scrollRef = useRef(null);

  const validationSchema = yup.object().shape({
    first_name: yup
      .string()
      .trim()
      .required(trans("Your first name is required")),
    last_name: yup
      .string()
      .trim()
      .required(trans("Your last name is required")),
    email: yup
      .string()
      .email(trans("Enter a valid email address"))
      .required(trans("Enter a valid email address")),
    phone_number: yup.string().phone(trans("Mobile number must be valid.")),
    business_name: yup
      .string()
      .trim()
      .required(trans("The name of your business is required")),
    select_all_that_apply: yup
      .array()
      .min(1, trans("Please select at least 1")),
    what_are_you_looking_for: yup
      .array()
      .min(1, trans("Please select at least 1")),
    where_did_you_hear_about_us: yup
      .array()
      .min(1, trans("Please select at least 1")),
    looking_for_other: yup.string().when("what_are_you_looking_for", {
      is: (what_are_you_looking_for) =>
        what_are_you_looking_for.includes("looking_for_other"),
      then: (schema) =>
        schema.trim().required(trans("Please complete this required field")),
    }),
    where_did_you_hear_about_us_other: yup
      .string()
      .when("where_did_you_hear_about_us", {
        is: (where_did_you_hear_about_us) =>
          where_did_you_hear_about_us.includes(
            "where_did_you_hear_about_us_other"
          ),
        then: (schema) =>
          schema.trim().required(trans("Please complete this required field")),
      }),
    existing_zbooni_account: yup
      .string()
      .required(trans("Please select an option")),
  });

  const handleSubmit = async (values) => {
    setIsLoading(true);

    if (!executeRecaptcha) {
      toast.error("reCaptcha not yet available. Please try again.");
      return;
    }

    const {
      what_are_you_looking_for,
      select_all_that_apply,
      where_did_you_hear_about_us_other,
      ...payload
    } = values;

    what_are_you_looking_for
      .filter((item) => item != "looking_for_other")
      .map((item) => {
        payload[item] = true;
      });

    select_all_that_apply.map((item) => {
      payload[item] = true;
    });

    const captchaToken = await executeRecaptcha("request_demo_form");
    const accessToken = await getAccessToken();
    const captchaRes = await verifyCaptcha(captchaToken, accessToken);

    if (!captchaRes?.success) {
      toast.error("An error has occurred. Please try again.");
      setIsLoading(false);
      return;
    }

    if (where_did_you_hear_about_us_other) {
      payload["where_did_you_hear_about_us"].splice(
        payload["where_did_you_hear_about_us"].indexOf(
          "where_did_you_hear_about_us_other"
        ),
        1,
        where_did_you_hear_about_us_other
      );
    }

    zapierClient
      .post(ZBNI_LEAD_GEN_ZAPIER_ENDPOINT, payload)
      .then(() => {
        setIsLoading(false);
        showSuccessPage(true);
        scrollRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      })
      .catch((err) => {
        toast.error(err.message);
        setIsLoading(false);
      });
  };

  return (
    <Layout headerIsOnTop={false} enableHeaderShrinkingOnScroll={false}>
      <div className="overflow-hidden bg-ghostwhite">
        <div ref={scrollRef} className="h-40 bg-primary pt-16 sm:h-60">
          <div className="zbooni-layout flex h-full items-center">
            <p className="text-2xl font-black leading-8 text-white sm:text-[38px]">
              <Trans>Request a demo</Trans>
            </p>
          </div>
        </div>
        <div
          className={classNames(
            "relative flex justify-center md:pb-32",
            successPage ? "" : "pb-32"
          )}
        >
          <div className="zbooni-layout absolute h-full w-full">
            <Sprite
              className="absolute -top-10 h-[200px] w-[400px] opacity-[3%] brightness-0 ltr:-left-16 rtl:-right-16 rtl:scale-x-[-1] md:h-[316px] md:w-[680px] ltr:md:-left-72 rtl:md:-right-72"
              name="zigzag-to-top-right"
            />
            <Sprite
              name="pricing-donut"
              className="absolute -bottom-48 h-[500px] w-[500px] ltr:-right-48 rtl:-left-48 md:-bottom-32 ltr:xl:-right-12 rtl:xl:-left-12"
            />
          </div>
          <div className="relative w-full bg-white sm:-top-10 sm:mx-7 sm:max-w-4xl sm:rounded-3xl lg:mx-20">
            {isLoading && (
              <div className="absolute h-full w-full rounded-3xl bg-white opacity-50" />
            )}
            {successPage ? (
              <div className="flex flex-col items-center justify-center gap-9 px-6 pb-56 pt-20 text-center sm:px-16 sm:py-14 md:gap-6 ">
                <Sprite name="zbooni-wink-smile" className="h-20 w-28" />
                <p className="max-w-xs text-2xl font-black leading-8 md:max-w-sm md:text-[35px] md:leading-10">
                  <Trans>Thank you for requesting a demo!</Trans>
                </p>
                <p className="text-lg font-medium leading-5 md:mt-5 md:text-[22px] md:leading-7">
                  <Trans>We will reach out to you in 48 hours or less.</Trans>
                </p>
                <Sprite
                  name="pricing-donut"
                  className="absolute -bottom-32 h-[320px] w-[320px] ltr:-right-16 rtl:-left-16 sm:hidden"
                />
              </div>
            ) : (
              <Formik
                initialValues={{
                  first_name: "",
                  last_name: "",
                  email: "",
                  phone_number: "",
                  business_name: "",
                  instagram_name: "",
                  has_trade_license: false,
                  has_website: false,
                  has_retail_location: false,
                  has_employees: false,
                  just_starting_out: false,
                  in_business_more_than_2_years: false,
                  better_payment_solutions: false,
                  marketing_tools_whatsapp: false,
                  enhancing_ecommerce: false,
                  extra_exposure: false,
                  looking_for_other: "",
                  notes: "",
                  select_all_that_apply: [],
                  what_are_you_looking_for: [],
                  where_did_you_hear_about_us: [],
                  existing_zbooni_account: "",
                }}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
              >
                {({ setFieldValue, isSubmitting }) => (
                  <Form className="px-6 py-7 sm:px-16 sm:py-14" noValidate>
                    <fieldset
                      disabled={isSubmitting || isLoading}
                      className="flex flex-col gap-10"
                    >
                      <div>
                        <p className="mb-5 text-sm font-extrabold leading-5">
                          <Trans>Tell us about yourself</Trans>
                        </p>
                        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                          <InputField
                            id="first_name"
                            name="first_name"
                            type="text"
                            label={trans("First Name")}
                            placeholder={trans("Please enter your first name")}
                          />
                          <InputField
                            id="last_name"
                            name="last_name"
                            type="text"
                            label={trans("Last Name")}
                            placeholder={trans("Please enter your last name")}
                          />
                          <InputField
                            id="email"
                            name="email"
                            type="email"
                            label={trans("Email")}
                            placeholder={trans("Please enter your email")}
                          />
                          <PhoneField
                            id="phone_number"
                            label={trans("Contact Number")}
                            name="phone_number"
                            defaultCountry="AE"
                          />
                        </div>
                      </div>
                      <div>
                        <p className="mb-5 text-sm font-extrabold leading-5">
                          <Trans>Tell us about your business</Trans>
                        </p>
                        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                          <InputField
                            id="business_name"
                            name="business_name"
                            type="text"
                            label={trans("Name of business")}
                            placeholder={trans(
                              "Please enter the business name"
                            )}
                          />
                          <InputField
                            id="instagram_name"
                            name="instagram_name"
                            type="text"
                            optional={true}
                            prefix="@"
                            label={trans("Instagram account")}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="mb-5 text-sm font-extrabold leading-5">
                          <Trans>Do you have an existing Zbooni account?</Trans>
                        </p>
                        <RadioGroup name="existing_zbooni_account">
                          <div className="flex gap-x-16 sm:gap-x-32">
                            <Radio
                              name="existing_zbooni_account"
                              value="true"
                              label={trans("Yes")}
                            />
                            <Radio
                              name="existing_zbooni_account"
                              value="false"
                              label={trans("No")}
                            />
                          </div>
                        </RadioGroup>
                      </div>
                      <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                        <CheckboxGroup
                          label={trans("Select all that apply")}
                          name="select_all_that_apply"
                        >
                          <div className="flex flex-col gap-4">
                            <Checkbox
                              name="select_all_that_apply"
                              value="has_trade_license"
                              label={trans("I have a trade license")}
                            />
                            <Checkbox
                              name="select_all_that_apply"
                              value="has_website"
                              label={trans("I have a website")}
                            />
                            <Checkbox
                              name="select_all_that_apply"
                              value="has_retail_location"
                              label={trans("I have a retail location")}
                            />
                            <Checkbox
                              name="select_all_that_apply"
                              value="has_employees"
                              label={trans("I have employees")}
                            />
                            <Checkbox
                              name="select_all_that_apply"
                              value="just_starting_out"
                              label={trans("I am just starting out")}
                            />
                            <Checkbox
                              name="select_all_that_apply"
                              value="in_business_more_than_2_years"
                              label={trans(
                                "I’ve been in business for more than 2 years"
                              )}
                            />
                          </div>
                        </CheckboxGroup>
                        <CheckboxGroup
                          label={trans("What are you looking for?")}
                          name="what_are_you_looking_for"
                        >
                          <div className="flex flex-col gap-4">
                            <Checkbox
                              name="what_are_you_looking_for"
                              value="better_payment_solutions"
                              label={trans("Better payment solutions")}
                            />
                            <Checkbox
                              name="what_are_you_looking_for"
                              value="marketing_tools_whatsapp"
                              label={trans("Marketing tools for WhatsApp")}
                            />
                            <Checkbox
                              name="what_are_you_looking_for"
                              value="enhancing_ecommerce"
                              label={trans(
                                "Enhancing my e-commerce capabilities"
                              )}
                            />
                            <Checkbox
                              name="what_are_you_looking_for"
                              value="extra_exposure"
                              label={trans("Extra exposure for my business")}
                            />
                            <div>
                              <Checkbox
                                name="what_are_you_looking_for"
                                value="looking_for_other"
                                label={trans("Other")}
                                onChange={() => {
                                  setFieldValue("looking_for_other", "");
                                  showLookingForOtherField(
                                    !lookingForOtherField
                                  );
                                }}
                              />
                              {lookingForOtherField && (
                                <div className="ltr:ml-5 rtl:mr-5">
                                  <InputField
                                    name="looking_for_other"
                                    type="text"
                                    placeholder={trans("Can you tell us more?")}
                                    additionalClassName="rounded-md"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </CheckboxGroup>
                      </div>
                      <div
                        className={classNames({
                          "sm:mb-5": hearAboutUsOtherField,
                        })}
                      >
                        <CheckboxGroup
                          label={trans("How did you hear about us?")}
                          name="where_did_you_hear_about_us"
                        >
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <Checkbox
                              name="where_did_you_hear_about_us"
                              value="Instagram"
                              label={trans("Instagram")}
                            />
                            <Checkbox
                              name="where_did_you_hear_about_us"
                              value="Facebook"
                              label={trans("Facebook")}
                            />
                            <Checkbox
                              name="where_did_you_hear_about_us"
                              value="LinkedIn"
                              label={trans("LinkedIn")}
                            />
                            <Checkbox
                              name="where_did_you_hear_about_us"
                              value="Search engine"
                              label={trans("Search engine")}
                            />
                            <Checkbox
                              name="where_did_you_hear_about_us"
                              value="App store"
                              label={trans("App store")}
                            />
                            <Checkbox
                              name="where_did_you_hear_about_us"
                              value="Blogs"
                              label={trans("Blogs")}
                            />
                            <Checkbox
                              name="where_did_you_hear_about_us"
                              value="Word of mouth"
                              label={trans("Word of mouth")}
                            />
                            <div className="relative">
                              <Checkbox
                                name="where_did_you_hear_about_us"
                                value="where_did_you_hear_about_us_other"
                                label={trans("Other")}
                                checked={hearAboutUsOtherField}
                                onChange={() => {
                                  setFieldValue(
                                    "where_did_you_hear_about_us_other",
                                    ""
                                  );
                                  showHearAboutUsOtherField(
                                    !hearAboutUsOtherField
                                  );
                                }}
                              />
                              {hearAboutUsOtherField && (
                                <div className="ltr:ml-5 rtl:mr-5 sm:absolute sm:w-[95%]">
                                  <InputField
                                    name="where_did_you_hear_about_us_other"
                                    type="text"
                                    placeholder={trans("Can you tell us more?")}
                                    additionalClassName="rounded-md"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </CheckboxGroup>
                      </div>
                      <div>
                        <TextArea
                          id="notes"
                          name="notes"
                          label={trans("Anything else you would like to add?")}
                          optional={true}
                        />
                      </div>
                      <div className="h-0.5 w-full bg-ghostwhite" />
                      <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                        <button
                          type="submit"
                          disabled={isLoading || isSubmitting}
                          className="col-start-1 h-12 w-full rounded-full bg-primary font-bold text-white disabled:bg-primary-light sm:col-start-2"
                        >
                          <Trans>Request a demo</Trans>
                        </button>
                      </div>
                    </fieldset>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default function RequestADemoPage() {
  const { locale: language } = useI18nLanguage();
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={ZBNI_RECAPTCHA_SITE_KEY}
      language={language}
    >
      <RequestADemo />
    </GoogleReCaptchaProvider>
  );
}
